import { useAuth0 } from '@auth0/auth0-react'
import { upload } from '@vercel/blob/client'

import { Env } from '@/config/env'

/**
 * Create a filesystem-safe-ish filename.
 *
 * @param {string} name
 * @returns {string}
 */
function sanitizeFilename(name) {
  return name
    .trim()
    .replace(/\s+/g, '-')
    .replace(/[^a-zA-Z0-9._-]/g, '')
}

/**
 * Extract a file extension without the leading dot.
 *
 * @param {string} name
 * @returns {string} extension without dot, or empty string
 */
function getFileExtension(name) {
  const idx = name.lastIndexOf('.')
  if (idx <= 0 || idx === name.length - 1) return ''
  return name.slice(idx + 1)
}

/**
 * Hook for uploading files to Vercel Blob via a server-side signed upload URL endpoint.
 *
 * Auth headers are added via Auth0 when enabled:
 * - Authorization: Bearer <access_token>
 * - X-ID-Token: <raw id token>
 *
 * If Auth0 is disabled, headers are omitted by default (recommended).
 * If your backend still requires auth in dev, add explicit env-based dev tokens.
 *
 * @returns {{ uploadVercelBlob: (file: File, filename?: string | null) => Promise<{ url: string }> }}
 */
export function useUploadVercelBlob() {
  const { getAccessTokenSilently, getIdTokenClaims } = useAuth0()

  /**
   * Upload a File to Vercel Blob.
   *
   * Requires a server endpoint to be set in the VITE_UPLOAD_URL environment
   * variable to generate a signed url for the upload.
   *
   * @param {File} file - The file to upload
   * @param {string | null} [filename] - Optional filename to use (will be sanitized)
   * @returns {Promise<{url: string}>}
   */
  const uploadVercelBlob = async (file, filename = null) => {
    const { uploadURL, authDisabled, devUploadAccessToken, devUploadIdToken } = Env

    if (!uploadURL) {
      throw new Error('VITE_UPLOAD_URL environment variable is required')
    }

    const finalName = (() => {
      if (filename) return sanitizeFilename(filename)

      const timestamp = Date.now()
      const ext = getFileExtension(file.name)
      const base = sanitizeFilename(file.name.replace(/\.[^/.]+$/, '')) || 'upload'
      return ext ? `${timestamp}-${base}.${ext}` : `${timestamp}-${base}`
    })()

    /** @type {Record<string, string>} */
    const headers = {}

    if (authDisabled) {
      if (devUploadAccessToken) headers.Authorization = `Bearer ${devUploadAccessToken}`
      if (devUploadIdToken) headers['X-ID-Token'] = devUploadIdToken
    } else {
      const accessToken = await getAccessTokenSilently()
      const idToken = (await getIdTokenClaims())?.__raw

      headers.Authorization = `Bearer ${accessToken}`
      if (idToken) headers['X-ID-Token'] = idToken
    }

    return upload(finalName, file, {
      access: 'public',
      handleUploadUrl: uploadURL,
      headers,
    })
  }

  return { uploadVercelBlob }
}
